import { Storage } from "aws-amplify";
import * as Yup from "yup";
import { QueryKeys } from "../../../constants/QueryKeys";
import moment from "moment";
import { regexPatterns } from "../../../utils/regexUtil";
import { stripCurrencyFormatting } from "../../../Utilities/regex";

export const submitModalConstants = {
  SUBMIT_TITLE: "Submit Ticket",
  SUBMIT_MESSAGE: "Are you sure you want to submit this ticket",
  APPROVE_TITLE: "Approve Ticket",
  APPROVE_MESSAGE: "Are you sure you want to approve this ticket",
  SUBMIT_BTN_TXT: "Confirm",
  WIDTH: 800,
};

export const reviewModalConstants = {
  TITLE: "Manager Review",
  SUBMIT_BTN_TXT: "Submit",
  TEXTBOX_LABEL: "Reason",
  TEXTBOX_PLACEHOLDER: "Enter Reason",
  TEXTBOX_NAME: "managerReviewReason",
  TEXTBOX_ROWS: 5,
  WIDTH: 800,
  DATA_INPUT_LIST_QUERY_KEY: QueryKeys.FETCH_TICKET_PROCESSING_REVIEW_LIST_KEY,
};

export const succesNotificationConstants = {
  TITLE: "Success",
  MESSAGE: "Your ticket has been submitted to manager review successfully",
};

export const sideBarConstants = {
  SEARCH_FIELD_NAME: "searchField",
  SEARCH_PLACEHOLDER: "Search",
  SIDE_PANEL_PENDING_TITLE: "Pending Data Inputs",
  SIDE_PANEL_MANAGER_REVIEW_TITLE: "Pending for manager review",
  SIDE_PANEL_SUBMITTED_TITLE: "Submitted Tickets",
};

export const replaceTktModalConstants = {
  TITLE: "Replace Ticket",
  SUBMIT_BTN_TXT: "Submit",
  REASON_FIELD_NAME: "replaceReason",
  REASON_FIELD_LABEL: "Reason",
  TEXTBOX_PLACEHOLDER: "Enter Reason",
  TEXTBOX_ROWS: 5,
  EXISTING_FILE_LABEL: "Existing File:",
  WIDTH: 800,
  DOWNLOAD: "Download",
  DOWNLOAD_BTN_CLASS: "downloadTktButton",
  REPLACE_TKT_ERROR:
    "Only single-page PDFs are allowed. Please upload a One-Page PDF",
  UPLOAD_DOC_MISSING_ERROR: "Please upload file containing replacement ticket",
  REPLACE_TKT_SUCCESS: "Ticket replaced successfully",
};

export const DraggerConstants = {
  UPLOAD_PRIMARY_TXT: "Click or drag file to this area to upload",
  UPLOAD_HINT: "Max file size is 5mb",
  MAX_FILE_COUNT: 1,
  DEFAULT_SHEET_STATE: "Pending",
  FILE_UPLOAD_SUCCESS_TXT: "Upload Completed!",
  PDF_ICON_ALT: "Pdf-icon",
  UPLOAD_ICON_ALT: "upload-icon",
};

export const FileUploadConstants = {
  DUPLICATE_WARNING: (fileName: string) =>
    `${fileName} is already uploaded. Do you want to upload it again?`,
  UPLOAD_SUCCESS: (fileName: string) => `${fileName} uploaded successfully`,
  UPLOAD_FAILED: (fileName: string) => `${fileName} upload failed`,
  DUPLICATE_TITLE: "Duplicate File",
  FILE_SIZE_WARNING: "File must be less than 5MB.",
  FILE_TYPE_WARNING_2: "Only PDF files are allowed.",
  FILE_TYPE_WARNING: "Only PDF files are allowed.",
  MAX_COUNT_WARNING: (maxCount: string) =>
    `You can only upload up to ${maxCount} file${
      Number(maxCount) > 1 ? "s" : ""
    }.`,
};

export const PDFOptionConstants = {
  ARCHIVE_OPTION_TEXT: "Archive",
  TKT_SUBMISSION_TXT: "Ticket submission",
  USER_TXT: "User",
};

export const buildTicketUrl = async (ticket) => {
  const filePath = ticket?.singlePdfTicket?.split("public/")?.[1];
  const fileURL = await Storage.get(filePath, {
    level: "public",
  });
  const cleanedUrl = fileURL?.split("?")?.[0];
  return {
    url: cleanedUrl,
    id: ticket?.ticketId,
  };
};

export const DataInputConstants = {
  USER_DETAILS_KEY: "userDetails",
  ID_VALUE_KEY: "id",
  SEARCH_ICON_ALT: "click to search",
  DATA_INPUT_LIST_QUERY_KEY: QueryKeys.FETCH_TICKET_PROCESSING_REVIEW_LIST_KEY,
  ERROR_MSG_SIGNED_BY: "Signed By customer must be checked",
  SUCCESS_HEADER: "Success",
  REVIEW_SUCCESS_MSG:
    "Your ticket has been submitted to manager review successfully",
  APPROVE_SUCCESS_MSG: "Your ticket has been approved successfully",
  SUBMIT_SUCESS_MSG: "Your ticket has been submitted successfully",
  RULE_ENGINE_ERROR:
    "You cannot submit the review according to Rule Engine Configuration",
  NO_DATA: "No data",
  NO_TICKETS_FOUND: "No tickets found",
};

export const successNotificationStyle = {
  backgroundColor: "#F1FFEE",
  borderRadius: "0.875rem",
  marginRight: "-1.875rem",
};

export const submitValidationSchema = Yup.object().shape({
  signedByCustomer: Yup.boolean().oneOf([true], ""),
  dailySummary: Yup.boolean().notRequired(),
  quantity: Yup.number()
    .transform((value, originalValue) => {
      // Handle empty values
      if (
        originalValue === "" ||
        originalValue === null ||
        originalValue === undefined
      ) {
        return null;
      }

      // Handle string inputs and remove any formatting
      if (typeof originalValue === "string") {
        const stripped = stripCurrencyFormatting(originalValue.trim());
        // Return undefined for empty strings after stripping
        if (stripped === "") {
          return null;
        }
        const num = Number(stripped);
        return isNaN(num) ? null : num;
      }

      return value;
    })
    .nullable()
    .when("dailySummary", {
      is: true,
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required("This is a required"),
    }),
  loadTime: Yup.string().notRequired(),
  startTime: Yup.string().when("dailySummary", {
    is: true,
    then: () =>
      Yup.string()
        .required("This is a required field")
        .test(
          "is-before-endTime",
          "Start time must be before end time",
          (value, context) => {
            const endTime = context.parent.endTime;
            if (!value || !endTime) return true;
            const start = moment(value, "HH:mm A");
            const end = moment(endTime, "HH:mm A");
            return start.isBefore(end);
          },
        ),
    otherwise: () => Yup.string().notRequired(),
  }),
  endTime: Yup.string().when("dailySummary", {
    is: true,
    then: () =>
      Yup.string()
        .required("This is a required field")
        .test(
          "is-after-startTime",
          "End time must be after start time",
          (value, context) => {
            const startTime = context.parent.startTime;
            if (!value || !startTime) return true;
            const start = moment(startTime, "HH:mm A");
            const end = moment(value, "HH:mm A");
            return end.isAfter(start);
          },
        ),
    otherwise: () => Yup.string().notRequired(),
  }),
  loadCount: Yup.number()
    .transform((value, originalValue) => {
      // Handle empty values
      if (
        originalValue === "" ||
        originalValue === null ||
        originalValue === undefined
      ) {
        return null;
      }

      // Handle string inputs and remove any formatting
      if (typeof originalValue === "string") {
        const stripped = stripCurrencyFormatting(originalValue.trim());
        // Return undefined for empty strings after stripping
        if (stripped === "") {
          return null;
        }
        const num = Number(stripped);
        return isNaN(num) ? null : num;
      }

      return value;
    })
    .nullable()
    .integer("Enter only integer value")
    .notRequired()
    .min(1, "The minimum load count is 1")
    .max(100, "The maximum load count is 100"),
  ticketDate: Yup.string().required("This is a required field"),
  ticketNumber: Yup.string()
    .required("This is a required field")
    .matches(regexPatterns.ticketNumberRegex, "Invalid ticket number"),
  truckNumber: Yup.string().required("This is a required field"),
});

export const reviewValidationSchema = Yup.object().shape({
  managerReviewReason: Yup.string().required("This is a required field"),
});

export const dummyArray = ["", "", "", "", ""];